import React, { useEffect, useState, useCallback } from 'react';
import { FTUETextBox } from './FTUETextBox';
import { FTUEStep } from '../../types/ftue.types';
import './FTUEHighlighter.scss';

interface FTUEHighlighterProps {
  step: FTUEStep;
  onClose: () => void;
  isLastStep?: boolean;
}

export const FTUEHighlighter: React.FC<FTUEHighlighterProps> = ({
  step,
  onClose,
  isLastStep = false,
}) => {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [highlightPosition, setHighlightPosition] = useState<DOMRect | null>(null);
  const [textBoxPosition, setTextBoxPosition] = useState<{
    top?: number;
    left?: number;
    right?: number;
    bottom?: number;
  }>({});

  const updatePositions = useCallback(() => {
    if (step.targetSelector) {
      const element = document.querySelector(step.targetSelector) as HTMLElement;
      if (element) {
        setTargetElement(element);
        const rect = element.getBoundingClientRect();
        setHighlightPosition(rect);

        // Calculate text box position based on step position preference
        const position = step.position || 'bottom';
        const offset = step.offset || { x: 0, y: 0 };
        const textBoxPositions = calculateTextBoxPosition(rect, position, offset);
        setTextBoxPosition(textBoxPositions);
      }
    }
  }, [step.targetSelector, step.position, step.offset]);

  useEffect(() => {
    updatePositions();
    
    // Update positions on window resize or scroll
    const handleResize = () => updatePositions();
    const handleScroll = () => updatePositions();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updatePositions]);

  const calculateTextBoxPosition = (
    targetRect: DOMRect,
    position: string,
    offset: { x?: number; y?: number }
  ) => {
    const textBoxMargin = 20;
    const offsetX = offset.x || 0;
    const offsetY = offset.y || 0;

    switch (position) {
      case 'top':
        return {
          bottom: window.innerHeight - targetRect.top + textBoxMargin + offsetY,
          left: targetRect.left + targetRect.width / 2 - 150 + offsetX, // 150 is half of textbox width
        };
      case 'bottom':
        return {
          top: targetRect.bottom + textBoxMargin + offsetY,
          left: targetRect.left + targetRect.width / 2 - 150 + offsetX,
        };
      case 'left':
        return {
          top: targetRect.top + targetRect.height / 2 - 25 + offsetY, // 50 is approximate half of textbox height
          right: window.innerWidth - targetRect.left + textBoxMargin - offsetX,
        };
      case 'right':
        return {
          top: targetRect.top + targetRect.height / 2 - 25 + offsetY,
          left: targetRect.right + textBoxMargin + offsetX,
        };
      default:
        return {
          top: targetRect.bottom + textBoxMargin + offsetY,
          left: targetRect.left + targetRect.width / 2 - 150 + offsetX,
        };
    }
  };

  if (!targetElement || !highlightPosition) {
    return null;
  }

  return (
    <>
      {/* Overlay that blocks interactions */}
      <div className="ftue-overlay" />
      
      {/* Highlight around target element */}
      <div
        className="ftue-highlight"
        style={{
          position: 'fixed',
          top: highlightPosition.top - 4,
          left: highlightPosition.left - 4,
          width: highlightPosition.width + 8,
          height: highlightPosition.height + 8,
          zIndex: 10000,
        }}
      />
      
      {/* Text box with instructions */}
      <FTUETextBox
        step={step}
        onClose={onClose}
        position={textBoxPosition}
        isLastStep={isLastStep}
      />
    </>
  );
};
